import React, { useState } from "react";
import { TTag } from "types/tag";

interface FilterRecipientsModalProps {
  tags: TTag[];
  isOpen: boolean;
  onClose: () => void;
  includeZipCodes: number[];
  setIncludeZipCodes: (zip: number[]) => void;
  excludeZipCodes: number[];
  setExcludeZipCodes: (zip: number[]) => void;
  includeCities: string[];
  setIncludeCities: (cities: string[]) => void;
  excludeCities: string[];
  setExcludeCities: (cities: string[]) => void;
  includeStates: string[];
  setIncludeStates: (states: string[]) => void;
  excludeStates: string[];
  setExcludeStates: (states: string[]) => void;
  includeTags: number[];
  setIncludeTags: (tags: number[]) => void;
  excludeTags: number[];
  setExcludeTags: (tags: number[]) => void;
}

const Badge = ({
  label,
  onRemove,
}: {
  label: string;
  onRemove: () => void;
}) => (
  <span className="inline-flex items-center gap-1 px-3 py-1 bg-orange-50 text-orange-800 text-sm rounded-full border border-orange-200">
    {label}
    <button
      type="button"
      className="text-orange-500 hover:text-orange-700 font-bold ml-1"
      onClick={onRemove}
    >
      &times;
    </button>
  </span>
);

const FilterRecipientsModal: React.FC<FilterRecipientsModalProps> = ({
  tags,
  isOpen,
  onClose,
  includeZipCodes,
  setIncludeZipCodes,
  includeCities,
  setIncludeCities,
  includeStates,
  setIncludeStates,
  includeTags,
  setIncludeTags,
}) => {
  const [zipInput, setZipInput] = useState("");
  const [cityInput, setCityInput] = useState("");
  const [stateInput, setStateInput] = useState("");
  const [tagSearch, setTagSearch] = useState("");
  const [tagDropdownOpen, setTagDropdownOpen] = useState(false);

  if (!isOpen) return null;

  const addZip = () => {
    const value = zipInput.trim();
    if (value && !includeZipCodes.includes(Number(value))) {
      setIncludeZipCodes([...includeZipCodes, Number(value)]);
      setZipInput("");
    }
  };

  const addCity = () => {
    const value = cityInput.trim();
    if (value && !includeCities.includes(value)) {
      setIncludeCities([...includeCities, value]);
      setCityInput("");
    }
  };

  const addState = () => {
    const value = stateInput.trim();
    if (value && !includeStates.includes(value)) {
      setIncludeStates([...includeStates, value]);
      setStateInput("");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, addFn: () => void) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addFn();
    }
  };

  const filteredTags = tags.filter(
    (tag) =>
      !includeTags.includes(Number(tag.id)) &&
      tag.label.toLowerCase().includes(tagSearch.toLowerCase())
  );

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-800">Filter Recipients</h2>
          <button className="text-gray-400 hover:text-gray-600 text-2xl" onClick={onClose}>
            &times;
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="p-6 overflow-y-auto flex-grow space-y-5">
          {/* ZIP Codes */}
          <div>
            <label className="block text-gray-700 font-medium mb-2">ZIP Codes</label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                className="flex-1 p-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#ff4f00]"
                value={zipInput}
                onChange={(e) => setZipInput(e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, addZip)}
                placeholder="Type ZIP and press Enter"
              />
            </div>
            {includeZipCodes.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {includeZipCodes.map((zip) => (
                  <Badge
                    key={zip}
                    label={String(zip)}
                    onRemove={() =>
                      setIncludeZipCodes(includeZipCodes.filter((z) => z !== zip))
                    }
                  />
                ))}
              </div>
            )}
          </div>

          {/* Cities */}
          <div>
            <label className="block text-gray-700 font-medium mb-2">Cities</label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                className="flex-1 p-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#ff4f00]"
                value={cityInput}
                onChange={(e) => setCityInput(e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, addCity)}
                placeholder="Type city and press Enter"
              />
            </div>
            {includeCities.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {includeCities.map((city) => (
                  <Badge
                    key={city}
                    label={city}
                    onRemove={() =>
                      setIncludeCities(includeCities.filter((c) => c !== city))
                    }
                  />
                ))}
              </div>
            )}
          </div>

          {/* States */}
          <div>
            <label className="block text-gray-700 font-medium mb-2">States</label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                className="flex-1 p-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#ff4f00]"
                value={stateInput}
                onChange={(e) => setStateInput(e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, addState)}
                placeholder="Type state and press Enter"
              />
            </div>
            {includeStates.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {includeStates.map((state) => (
                  <Badge
                    key={state}
                    label={state}
                    onRemove={() =>
                      setIncludeStates(includeStates.filter((s) => s !== state))
                    }
                  />
                ))}
              </div>
            )}
          </div>

          {/* Tags */}
          <div className="relative">
            <label className="block text-gray-700 font-medium mb-2">Tags</label>
            <input
              type="text"
              className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#ff4f00]"
              value={tagSearch}
              onChange={(e) => {
                setTagSearch(e.target.value);
                setTagDropdownOpen(true);
              }}
              onFocus={() => setTagDropdownOpen(true)}
              placeholder="Search tags..."
            />
            {tagDropdownOpen && tagSearch.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {filteredTags.length > 0 ? (
                  filteredTags.map((tag) => (
                    <button
                      key={tag.id}
                      type="button"
                      className="w-full text-left px-3 py-2 text-sm hover:bg-[#ff4f00] hover:text-white transition-colors"
                      onClick={() => {
                        setIncludeTags([...includeTags, Number(tag.id)]);
                        setTagSearch("");
                        setTagDropdownOpen(false);
                      }}
                    >
                      {tag.label}
                    </button>
                  ))
                ) : (
                  <p className="px-3 py-2 text-sm text-gray-400">No tags found</p>
                )}
              </div>
            )}
            {includeTags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {includeTags.map((tagId) => {
                  const tag = tags.find((t) => Number(t.id) === tagId);
                  return (
                    <Badge
                      key={tagId}
                      label={tag?.label || String(tagId)}
                      onRemove={() =>
                        setIncludeTags(includeTags.filter((id) => id !== tagId))
                      }
                    />
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t flex justify-end gap-4">
          <button
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 bg-[#ff4f00] text-white rounded-lg hover:bg-[#e04500] transition-colors"
            onClick={onClose}
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilterRecipientsModal;
